import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2, Mail } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_EMAIL = "sporter@socialimpactcle.com";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in every field.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("Thank you! Your message has been sent.");
      setTimeout(() => setDone(false), 4000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-white py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-24 items-start">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-brand-teal mb-5 flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-brand-teal" />
              Contact
            </p>
            <h2 className="font-display font-black tracking-tighter text-charcoal text-4xl md:text-6xl leading-[0.95]">
              Let&apos;s build
              <br />
              <span className="text-brand-orange">impact together.</span>
            </h2>
            <p className="mt-6 text-lg text-charcoal/65 leading-relaxed max-w-md">
              Have a question, partnership idea, or want to get involved? We&apos;d
              love to hear from you.
            </p>

            <p className="mt-5 text-base text-charcoal/70">
              Or reach us directly at{" "}
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="contact-email-link"
                className="group inline-flex items-center gap-1.5 font-semibold text-brand-orange underline-offset-4 hover:underline"
              >
                <Mail size={16} />
                {CONTACT_EMAIL}
              </a>
            </p>

            <div className="mt-10 hidden lg:block">
              <div className="rounded-3xl bg-warmgray p-8 border border-black/5">
                <p className="font-mono text-xs uppercase tracking-widest text-charcoal/45 mb-2">
                  Rooted in
                </p>
                <p className="font-display font-bold text-2xl text-navy">
                  Cleveland, Ohio 🇺🇸
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form
              data-testid="contact-form"
              onSubmit={onSubmit}
              className="rounded-3xl bg-navy p-8 md:p-10 shadow-[0_40px_80px_-40px_rgba(27,42,74,0.6)]"
            >
              <div className="space-y-6">
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-white/60 mb-2">
                    Name
                  </label>
                  <input
                    data-testid="contact-name"
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Your name"
                    className="w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 text-white placeholder:text-white/35 outline-none transition-all duration-300 focus:border-brand-orange focus:bg-white/10"
                  />
                </div>
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-white/60 mb-2">
                    Email
                  </label>
                  <input
                    data-testid="contact-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    className="w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 text-white placeholder:text-white/35 outline-none transition-all duration-300 focus:border-brand-orange focus:bg-white/10"
                  />
                </div>
                <div>
                  <label className="block font-mono text-xs uppercase tracking-widest text-white/60 mb-2">
                    Message
                  </label>
                  <textarea
                    data-testid="contact-message"
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us how you'd like to get involved…"
                    className="w-full resize-none rounded-xl bg-white/5 border border-white/15 px-4 py-3.5 text-white placeholder:text-white/35 outline-none transition-all duration-300 focus:border-brand-orange focus:bg-white/10"
                  />
                </div>

                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading || done}
                  className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(232,98,26,0.7)] disabled:opacity-70 disabled:translate-y-0"
                >
                  {loading ? (
                    <>
                      <Loader2 size={20} className="animate-spin" /> Sending…
                    </>
                  ) : done ? (
                    <>
                      <CheckCircle2 size={20} /> Message Sent
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send
                        size={18}
                        className="transition-transform duration-300 group-hover:translate-x-1"
                      />
                    </>
                  )}
                </button>
              </div>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
